import { useEffect, useState, useMemo } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { DndContext, DragOverlay, PointerSensor, useSensor, useSensors, useDraggable, useDroppable } from "@dnd-kit/core";
import { GripVertical, User, MapPin, Search, Check, UserCog, CheckCircle2, Hand, X } from "lucide-react";
import { toast } from "sonner";
import { motion } from "framer-motion";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import TicketForm from "@/components/TicketForm";
import RouteButtons from "@/components/RouteButtons";
import { sortRecentTicketsDesc } from "@/lib/ticketSort";
import { formatWIBDateTime24Short } from "@/lib/time";
import { themeForColor } from "@/lib/statusTheme";

// Kanban columns pull their theme from the shared statusTheme helper so
// admins can recolor status pills in Pengaturan Aplikasi and the whole app
// (Kanban, Tickets list, Dashboard, TicketMap) updates on the next render.
// `toColumn` accepts the raw master row and resolves its palette lazily.
function toColumn({ code, label, color }) {
  const t = themeForColor(color || _DEFAULT_COLOR_FOR_CODE[code] || "slate");
  return { key: code, label: label || code, color: color, accent: t.accent, bg: t.bg };
}

// Bootstrap fallback used only for the initial paint (before /api/reference
// resolves). Mirrors backend DEFAULT_STATUS ordering + colors.
const _DEFAULT_COLOR_FOR_CODE = {
  OPEN: "amber", SCHEDULED: "violet", PROSES: "blue", SURVEY: "cyan",
  WAITING: "orange", RESCHEDULE: "indigo", DONE: "green", CANCEL: "red",
};

// Bootstrap columns for the first paint (before /api/reference resolves).
// Mirrors the backend DEFAULT_STATUS order so the initial layout matches what
// the master data would return in a fresh database.
const DEFAULT_COLUMNS = [
  { code: "OPEN",       label: "Baru / Open",       color: "amber" },
  { code: "SCHEDULED",  label: "Dijadwalkan",       color: "violet" },
  { code: "PROSES",     label: "Sedang Dikerjakan", color: "blue" },
  { code: "SURVEY",     label: "Survey Awal",       color: "cyan" },
  { code: "WAITING",    label: "Menunggu",          color: "orange" },
  { code: "RESCHEDULE", label: "Dijadwal Ulang",    color: "indigo" },
  { code: "DONE",       label: "Selesai",           color: "green" },
  { code: "CANCEL",     label: "Dibatalkan",        color: "red" },
].map(toColumn);

const PRIORITY_COLOR = { P1: "#991B1B", P2: "#EA580C", P3: "#0055FF", P4: "#4B5563" };

function AssignPopover({ ticket, users, canAssign, onAssign }) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return users;
    return users.filter((u) => u.name.toLowerCase().includes(s) || u.username.toLowerCase().includes(s));
  }, [q, users]);

  if (!canAssign) {
    return (
      <span className="flex items-center gap-1 min-w-0 text-[11px] text-[#4B5563]">
        <User className="h-3 w-3 flex-shrink-0" />
        <span className="truncate">{ticket.pic || "-"}</span>
      </span>
    );
  }
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          onClick={(e) => e.stopPropagation()}
          data-testid={`assign-trigger-${ticket.id}`}
          className={`flex items-center gap-1 min-w-0 text-[11px] px-1.5 py-0.5 rounded transition-colors ${
            ticket.pic ? "text-[#4B5563] hover:bg-[#F3F4F6]" : "text-[#EA580C] hover:bg-[#EA580C]/10 font-semibold"
          }`}
        >
          <User className="h-3 w-3 flex-shrink-0" />
          <span className="truncate">{ticket.pic || "Assign PIC"}</span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-64 p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
        onPointerDown={(e) => e.stopPropagation()}
        data-testid={`assign-panel-${ticket.id}`}
      >
        <div className="px-3 py-2 border-b border-[#E5E7EB] flex items-center gap-2">
          <UserCog className="h-3.5 w-3.5 text-[#9CA3AF]" />
          <span className="text-[10px] font-bold uppercase tracking-widest text-[#4B5563]">Assign PIC</span>
        </div>
        <div className="p-2 border-b border-[#E5E7EB]">
          <div className="relative">
            <Search className="h-3.5 w-3.5 absolute left-2 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Cari nama teknisi..."
              className="w-full h-8 pl-7 pr-2 rounded border border-[#E5E7EB] text-xs focus:outline-none focus:ring-2 focus:ring-[#0055FF] focus:border-transparent"
              data-testid={`assign-search-${ticket.id}`}
            />
          </div>
        </div>
        <div className="max-h-64 overflow-y-auto">
          {ticket.pic && (
            <button
              onClick={async () => { await onAssign(ticket, null); setOpen(false); }}
              className="w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-[#F8F9FA] text-[#991B1B] border-b border-[#F3F4F6]"
              data-testid={`assign-clear-${ticket.id}`}
            >
              Hapus penugasan
            </button>
          )}
          {filtered.map((u) => {
            const active = u.name === ticket.pic;
            return (
              <button
                key={u.id}
                onClick={async () => { await onAssign(ticket, u.name); setOpen(false); }}
                data-testid={`assign-option-${ticket.id}-${u.username}`}
                className={`w-full flex items-center justify-between px-3 py-2 text-xs hover:bg-[#F8F9FA] ${active ? "bg-[#0055FF]/[0.06]" : ""}`}
              >
                <div className="flex flex-col items-start min-w-0">
                  <span className="font-semibold text-[#0A0A0A] truncate">{u.name}</span>
                  <span className="text-[10px] text-[#9CA3AF] font-mono-tab uppercase tracking-wider">{u.role} · {u.username}</span>
                </div>
                {active && <Check className="h-3.5 w-3.5 text-[#0055FF] flex-shrink-0" />}
              </button>
            );
          })}
          {filtered.length === 0 && (
            <div className="px-3 py-4 text-center text-xs text-[#9CA3AF]">Tidak ada hasil</div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

function TicketCard({ ticket, users, canAssign, onOpen, onAssign, overlay = false }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({ id: ticket.id });
  return (
    <div
      ref={setNodeRef}
      {...attributes}
      style={{ opacity: isDragging && !overlay ? 0.35 : 1 }}
      className={`group bg-white border border-[#E5E7EB] rounded-md p-3 hover:border-[#0055FF] transition-colors shadow-sm ${overlay ? "shadow-lg rotate-2 cursor-grabbing" : "cursor-default"}`}
      data-testid={`kanban-card-${ticket.id}`}
    >
      <div className="flex items-start gap-2">
        <button
          {...listeners}
          className="text-[#D1D5DB] hover:text-[#4B5563] cursor-grab active:cursor-grabbing pt-0.5"
          data-testid={`kanban-drag-${ticket.id}`}
          aria-label="Drag"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-1" onClick={onOpen} role="button" tabIndex={0}>
            <span className="font-mono-tab font-bold text-xs text-[#0A0A0A] truncate">{ticket.no_tiket || "-"}</span>
            {ticket.prioritas && (
              <span
                className="text-[9px] font-black uppercase tracking-widest px-1.5 py-0.5 rounded"
                style={{ color: PRIORITY_COLOR[ticket.prioritas] || "#4B5563", background: (PRIORITY_COLOR[ticket.prioritas] || "#4B5563") + "14" }}
              >
                {ticket.prioritas}
              </span>
            )}
          </div>
          <div className="text-sm text-[#0A0A0A] mt-1 line-clamp-2" onClick={onOpen} role="button" tabIndex={0}>
            {ticket.objek_lokasi || "—"}
          </div>
          <div className="flex items-center gap-3 mt-2 text-[11px] text-[#4B5563]">
            <AssignPopover ticket={ticket} users={users} canAssign={canAssign && !overlay} onAssign={onAssign} />
            {ticket.wilayah && (
              <span className="flex items-center gap-1 min-w-0">
                <MapPin className="h-3 w-3 flex-shrink-0" />
                <span className="truncate">{ticket.wilayah}</span>
              </span>
            )}
          </div>
          {ticket.status === "DONE" && ticket.completed_at && (
            <div
              className="mt-1.5 flex items-center gap-1 text-[11px] text-[#059669] font-mono-tab"
              data-testid={`kanban-done-time-${ticket.id}`}
            >
              <CheckCircle2 className="h-3 w-3 flex-shrink-0" aria-hidden />
              <span className="truncate">
                Selesai {formatWIBDateTime24Short(ticket.completed_at)}
              </span>
            </div>
          )}
          {(ticket.lat || ticket.wilayah) && !overlay && (
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              <RouteButtons ticket={ticket} size="xs" showLabel={false} />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Column({ col, tickets, users, canAssign, onOpen, onAssign }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.key });
  return (
    <div
      ref={setNodeRef}
      className={`flex flex-col rounded-md border transition-colors ${isOver ? "border-[#0055FF] bg-[#0055FF]/[0.03]" : "border-[#E5E7EB] bg-[#F8F9FA]"}`}
      data-testid={`kanban-col-${col.key}`}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-[#E5E7EB]">
        <div className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: col.accent }} />
          <span className="text-[11px] font-bold uppercase tracking-widest text-[#0A0A0A]">{col.label}</span>
        </div>
        <span
          className="font-mono-tab text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{ background: col.bg, color: col.accent }}
          data-testid={`kanban-count-${col.key}`}
        >
          {tickets.length}
        </span>
      </div>
      <div className="p-2 space-y-2 min-h-24 flex-1 overflow-y-auto max-h-[420px] lg:max-h-[calc(50vh-80px)]">
        {tickets.map((t) => (
          <TicketCard
            key={t.id}
            ticket={t}
            users={users}
            canAssign={canAssign}
            onOpen={() => onOpen(t)}
            onAssign={onAssign}
          />
        ))}
        {tickets.length === 0 && (
          <div className="text-[11px] text-[#9CA3AF] text-center py-6">
            Tidak ada tiket
          </div>
        )}
      </div>
    </div>
  );
}

function MobileStatusView({ columns, grouped, onOpen, onChangeStatus }) {
  const [selected, setSelected] = useState(columns[0]?.key || "");
  const activeCol = columns.find((c) => c.key === selected) || columns[0];
  const list = grouped[selected] || [];
  return (
    <div className="md:hidden" data-testid="mobile-kanban">
      <div className="grid grid-cols-3 gap-2" data-testid="mobile-status-selector">
        {columns.map((c) => {
          const active = c.key === selected;
          const count = (grouped[c.key] || []).length;
          return (
            <button
              key={c.key}
              type="button"
              onClick={() => setSelected(c.key)}
              data-testid={`mobile-status-${c.key}`}
              aria-pressed={active}
              className={`flex flex-col items-start gap-0.5 rounded-md border px-2 py-2 text-left transition-transform duration-100 active:scale-[0.97] ${
                active
                  ? "border-[#0A0A0A] bg-[#0A0A0A] text-white shadow-sm"
                  : "border-[#E5E7EB] bg-white hover:bg-[#F8F9FA] text-[#0A0A0A]"
              }`}
            >
              <div className="flex items-center gap-1.5 min-w-0 w-full">
                <span
                  className="h-2 w-2 rounded-full flex-shrink-0"
                  style={{ background: c.accent }}
                />
                <span className={`text-[10px] font-bold uppercase tracking-wider truncate ${active ? "text-white" : "text-[#4B5563]"}`}>
                  {c.label}
                </span>
              </div>
              <span
                className={`font-mono-tab font-black text-lg leading-none ${active ? "text-white" : "text-[#0A0A0A]"}`}
              >
                {count}
              </span>
            </button>
          );
        })}
      </div>

      <div className="mt-4 mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className="h-2 w-2 rounded-full flex-shrink-0" style={{ background: activeCol.accent }} />
          <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#0A0A0A]">
            {activeCol.label}
          </span>
        </div>
        <span
          className="font-mono-tab text-[10px] font-bold px-2 py-0.5 rounded-full"
          style={{ background: activeCol.bg, color: activeCol.accent }}
        >
          {list.length} tiket
        </span>
      </div>

      <ul className="space-y-2" data-testid="mobile-ticket-list">
        {list.length === 0 && (
          <li className="text-center text-xs text-[#9CA3AF] py-8">Tidak ada tiket</li>
        )}
        {list.map((t) => (
          <li
            key={t.id}
            role="button"
            tabIndex={0}
            onClick={() => onOpen(t)}
            onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(t); } }}
            data-testid={`mobile-tk-${t.id}`}
            className="bg-white border border-[#E5E7EB] rounded-md p-3 cursor-pointer transition-transform duration-100 active:scale-[0.985] active:bg-[#F8F9FA] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]"
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-mono-tab font-semibold text-[13px] text-[#0A0A0A] truncate">
                {t.no_tiket || "-"}
              </span>
              {t.prioritas && (
                <span className="font-bold font-mono-tab text-[11px]" style={{ color: PRIORITY_COLOR[t.prioritas] || "#4B5563" }}>
                  {t.prioritas}
                </span>
              )}
            </div>
            <div className="mt-1 text-sm text-[#0A0A0A] font-medium line-clamp-2 break-words">
              {t.objek_lokasi || "-"}
            </div>
            <div className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-[#4B5563]">
              {t.wilayah && <span>{t.wilayah}</span>}
              {t.pic && <span className="text-[#9CA3AF]">·</span>}
              {t.pic && <span className="truncate max-w-[55%]">{t.pic}</span>}
              {t.jenis_pekerjaan && <span className="text-[#9CA3AF]">·</span>}
              {t.jenis_pekerjaan && (
                <span className="text-[10px] font-bold uppercase tracking-widest text-[#4B5563] bg-[#F3F4F6] px-1.5 py-0.5 rounded">
                  {t.jenis_pekerjaan}
                </span>
              )}
            </div>
            {t.status === "DONE" && t.completed_at && (
              <div
                className="mt-1.5 flex items-center gap-1 text-[11px] text-[#059669] font-mono-tab"
                data-testid={`mobile-done-time-${t.id}`}
              >
                <CheckCircle2 className="h-3 w-3 flex-shrink-0" aria-hidden />
                <span className="truncate">
                  Selesai {formatWIBDateTime24Short(t.completed_at)}
                </span>
              </div>
            )}
            <div className="mt-2" onClick={(e) => e.stopPropagation()}>
              <label className="text-[10px] font-bold uppercase tracking-widest text-[#9CA3AF]">Ubah Status</label>
              <select
                value={t.status}
                onChange={(e) => onChangeStatus(t, e.target.value)}
                data-testid={`mobile-status-change-${t.id}`}
                className="mt-1 w-full h-9 rounded-md border border-[#E5E7EB] bg-white px-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]"
              >
                {columns.map((c) => (
                  <option key={c.key} value={c.key}>{c.label}</option>
                ))}
              </select>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

export default function Kanban() {
  const { user } = useAuth();
  const canAssign = user?.role === "admin" || user?.role === "koordinator";
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  // Kanban columns are derived from the shared master_status list (served by
  // /api/reference). That way, when an admin adds/removes a status in
  // Pengaturan Aplikasi it flows straight into the board — SURVEY included —
  // without a code change. The DEFAULT_COLUMNS array is only used until the
  // first reference load resolves so the initial paint isn't blank.
  const [columns, setColumns] = useState(() => DEFAULT_COLUMNS);
  const [activeId, setActiveId] = useState(null);
  const [dialogTicket, setDialogTicket] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  // Onboarding hint: purely local state — resets to `true` every time the
  // Kanban page mounts (visit / navigate back / refresh / re-login). No
  // localStorage, no sessionStorage, no backend persistence. The dismiss
  // action just flips this in-memory flag; a subsequent successful drag
  // (see onDragEnd) does the same.
  const [showDragHint, setShowDragHint] = useState(true);
  const dismissDragHint = () => setShowDragHint(false);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 4 } }));

  const load = async () => {
    try {
      const { data } = await api.get("/tickets", { params: { limit: 500 } });
      setTickets(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    }
  };

  const loadUsers = async () => {
    if (!canAssign) return;
    try {
      const { data } = await api.get("/users/lookup");
      setUsers(data);
    } catch (err) {
      console.error("[Kanban] failed to load users:", err);
    }
  };

  const loadColumns = async () => {
    try {
      const { data } = await api.get("/reference");
      const list = Array.isArray(data?.status) ? data.status : [];
      if (list.length) setColumns(list.map(toColumn));
    } catch (err) {
      // Silent fallback — DEFAULT_COLUMNS already covers the canonical set,
      // and the tickets endpoint remains authoritative for what actually
      // exists in the board.
      console.error("[Kanban] failed to load status reference:", err);
    }
  };

  useEffect(() => { load(); loadUsers(); loadColumns(); /* eslint-disable-next-line */ }, [canAssign]);

  const onAssign = async (ticket, name) => {
    const prev = ticket.pic;
    setTickets((all) => all.map((x) => (x.id === ticket.id ? { ...x, pic: name || null } : x)));
    try {
      await api.patch(`/tickets/${ticket.id}`, { pic: name });
      toast.success(name ? `${ticket.no_tiket} → ${name}` : "Penugasan dihapus");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setTickets((all) => all.map((x) => (x.id === ticket.id ? { ...x, pic: prev } : x)));
    }
  };

  const grouped = useMemo(() => {
    const g = Object.fromEntries(columns.map((c) => [c.key, []]));
    tickets.forEach((t) => { if (g[t.status]) g[t.status].push(t); });
    // Each column must show newest → oldest by tgl_masuk (created_at fallback).
    // Sort per-column so drag/drop between columns keeps the invariant.
    for (const k of Object.keys(g)) g[k] = sortRecentTicketsDesc(g[k]);
    return g;
  }, [tickets, columns]);

  const activeTicket = activeId ? tickets.find((t) => t.id === activeId) : null;

  const changeStatus = async (ticket, dest) => {
    if (!ticket || ticket.status === dest) return;
    const prev = ticket.status;
    setTickets((all) => all.map((x) => (x.id === ticket.id ? { ...x, status: dest } : x)));
    try {
      await api.patch(`/tickets/${ticket.id}`, { status: dest });
      toast.success(`${ticket.no_tiket || "Tiket"} → ${dest}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
      setTickets((all) => all.map((x) => (x.id === ticket.id ? { ...x, status: prev } : x)));
    }
  };

  const onDragEnd = async (event) => {
    const { active, over } = event;
    setActiveId(null);
    if (!over) return;
    const t = tickets.find((x) => x.id === active.id);
    if (!t) return;
    // A successful cross-column drag is the strongest signal the user has
    // understood the feature — retire the hint without waiting for the close X.
    if (t.status !== over.id && showDragHint) dismissDragHint();
    await changeStatus(t, over.id);
  };

  return (
    <div className="space-y-6" data-testid="kanban-page">
      <div className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.25em] text-[#9CA3AF]">Daftar Tugas</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter mt-1">Status Pekerjaan</h1>
          <p className="text-sm text-[#4B5563] mt-2">
            Pantau status pekerjaan dan pilih tiket untuk melihat detail lengkap.
          </p>
        </div>
        <div className="text-right text-xs text-[#9CA3AF]">
          <div className="font-mono-tab font-bold text-2xl text-[#0A0A0A]">{tickets.length}</div>
          <div className="uppercase tracking-widest text-[10px] font-bold">total tiket</div>
        </div>
      </div>

      {/* First-run onboarding hint. Non-intrusive banner; auto-retires on
          successful drag or explicit dismiss. Persists via localStorage. */}
      {showDragHint && (
        <div
          className="flex items-center gap-3 rounded-md border border-[#DBE4FF] bg-[#F5F8FF] px-3 py-2 shadow-sm"
          role="note"
          aria-live="polite"
          data-testid="kanban-drag-hint"
        >
          <span className="flex h-7 w-7 items-center justify-center rounded-md bg-white border border-[#DBE4FF] text-[#0055FF] flex-shrink-0">
            <Hand className="h-4 w-4" aria-hidden />
          </span>
          <p className="text-xs sm:text-sm text-[#0A0A0A] leading-snug flex-1 min-w-0">
            <span className="font-semibold">Seret kartu ke kolom lain</span>
            <span className="text-[#0055FF] font-bold" aria-hidden>{" "}→</span>
            <span className="text-[#4B5563]">
              {" "}untuk mengubah status. Klik kartu untuk detail.
            </span>
          </p>
          <button
            type="button"
            onClick={dismissDragHint}
            className="flex h-7 w-7 items-center justify-center rounded-md text-[#9CA3AF] hover:text-[#0A0A0A] hover:bg-white flex-shrink-0 focus:outline-none focus-visible:ring-2 focus-visible:ring-[#0055FF]"
            data-testid="kanban-drag-hint-close"
            aria-label="Tutup petunjuk"
          >
            <X className="h-4 w-4" aria-hidden />
          </button>
        </div>
      )}

      <DndContext sensors={sensors} onDragStart={(e) => setActiveId(e.active.id)} onDragEnd={onDragEnd} onDragCancel={() => setActiveId(null)}>
        {/* Desktop: 2×3 grid (2 rows of 3 primary statuses; CANCEL wraps to next row on xl) */}
        <motion.div
          initial={{ opacity: 0, y: 8 }}
          animate={{ opacity: 1, y: 0 }}
          className="hidden md:grid md:grid-cols-3 xl:grid-cols-4 gap-3"
        >
          {columns.map((c) => (
            <Column
              key={c.key}
              col={c}
              tickets={grouped[c.key] || []}
              users={users}
              canAssign={canAssign}
              onAssign={onAssign}
              onOpen={(t) => { setDialogTicket(t); setDialogOpen(true); }}
            />
          ))}
        </motion.div>
        <DragOverlay>
          {activeTicket ? <TicketCard ticket={activeTicket} users={users} canAssign={false} overlay /> : null}
        </DragOverlay>
      </DndContext>

      {/* Mobile: status selector chips + vertical card list */}
      <MobileStatusView
        columns={columns}
        grouped={grouped}
        users={users}
        canAssign={canAssign}
        onAssign={onAssign}
        onChangeStatus={changeStatus}
        onOpen={(t) => { setDialogTicket(t); setDialogOpen(true); }}
      />

      <TicketForm
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        initial={dialogTicket}
        readOnly={user?.role === "teknisi" && dialogTicket?.pic !== user?.name}
        onSaved={load}
      />
    </div>
  );
}
